import { useState, useMemo, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Stack,
  Paper,
  IconButton,
  Typography,
  Button,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  MenuItem,
  TextField,
  Breadcrumbs,
  Link,
  Stepper,
  Step,
  StepLabel,
  Chip,
  Alert,
  Collapse,
  Divider,
  Card,
  Grid
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ChevronRight as ChevronRightIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  MoreHoriz as MoreHorizIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Delete as DeleteIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useForm } from 'react-hook-form';
import dayjs from 'dayjs';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import CustomDate from '../../components/CustomDate';
import { setApiError } from '../../store/Slice/Error/ErrorSlice';
import { DataSourceApi } from '../../utils/Apis';
import Page from '../../components/Page';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import {
  getCityData,
  getInternalSaleManager,
  getLoanTypeData,
  saveApiData
} from '../../store/Slice/Global/GlobalSlice';
import useGlobalSelector from '../../store/Slice/Global/GlobalSelector';
import CustomSelect from '../../components/CustomSelect';
import { getAgentList } from '../../store/Slice/DataSource/DataSourceSlice';
import useDataSourceSelector from '../../store/Slice/DataSource/DataSourceSelector';

const THEME = {
  // pageBg: '#f4f6f8',
  cyan: '#4fbbc8',
  cyanPill: '#4fbbc8',
  cardBg: '#ffffff',
  textDark: '#0f172a',
  textMuted: '#64748b',
  border: '#e2e8f0',
  saveBtn: '#0d4689',
  primary: '#0d4689',
  success: '#10b981'
};

const attributes = [
  'loanType',
  'cibilScore', // Days Past Due - critical for recovery routing
  'loadAmount' // Different products have different processing rules
];

// Comparison operators for trigger condition values
const operators = ['EqualTo', '>', '<', 'Between', 'In', 'Not In'];

// Logical connectors for multiple conditions
const logicalOps = ['AND', 'OR'];

// Styling constants
const labelSx = {
  mb: 0.8,
  display: 'block',
  fontFamily: 'Poppins, sans-serif',
  color: THEME.textMuted,
  fontSize: 13,
  fontWeight: 500
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '6px',
    height: 42,
    bgcolor: '#ffffff',
    transition: 'all 0.3s ease',
    '& input': {
      padding: '10px 12px',
      fontFamily: 'Poppins, sans-serif',
      fontSize: 14,
      color: THEME.textDark
    },
    '& fieldset': { borderColor: THEME.border },
    '&:hover fieldset': { borderColor: THEME.cyan },
    '&.Mui-focused fieldset': { borderColor: THEME.cyan, borderWidth: '1px' },
    '& .MuiInputBase-input::placeholder': {
      color: '#94a3b8',
      opacity: 1
    }
  }
};

const selectSx = {
  borderRadius: '6px',
  height: 42,
  bgcolor: '#ffffff',
  fontFamily: 'Poppins, sans-serif',
  fontSize: 14,
  transition: 'all 0.3s ease',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: THEME.border },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: THEME.cyan },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: THEME.cyan,
    borderWidth: '1px'
  },
  '& .MuiSelect-select': {
    padding: '10px 12px',
    display: 'flex',
    alignItems: 'center'
  }
};

const steps = ['Select Trigger Condition', 'Select Person', 'Select Timeline'];

const mapOperator = (op) => {
  if (op === 'Equal To') return 'EqualTo';
  return op;
};

const validateDateRange = (start, end) => {
  if (!start || !end) return true;
  return dayjs(start).isBefore(dayjs(end));
};

export default function Allocation() {
  const navigate = useNavigate();
  const [ruleType, setRuleType] = useState('new');
  const [activeStep, setActiveStep] = useState(0);
  const [currentAgent, setCurrentAgent] = useState('');
  const [selectedAgents, setSelectedAgents] = useState([]);

  const dispatch = useDispatch();
  const { LoginData } = useLoginSelector();
  const { DataAgentList } = useDataSourceSelector();

  // console.log('DataAgentList222', DataAgentList);
  const location = useLocation();
  const { rowId } = location.state || {};

  const {
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
    formState: { errors }
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      datasetName: ''
    }
  });
  const selectedCity = watch('cityName');
  // Reassign State - for reassigning existing loans between agents
  const [reassignData, setReassignData] = useState({
    assignTo: '',
    reassignTo: '',
    startDate: null,
    endDate: null
  });

  // Step 1: Trigger Conditions - Banking logic for loan routing
  // These conditions determine which loans match the rule
  const [conditions, setConditions] = useState([
    {
      id: 1,
      attribute: '',
      operator: '',
      value: '',
      logical: 'AND'
    }
  ]);

  const [timeline, setTimeline] = useState({
    startDate: null,
    endDate: null,
    occurrence: 'One-time',
    time: ''
  });
  const [personFilters, setPersonFilters] = useState({ dept: '', loc: '' });

  const agentListApi = useCallback(() => {
    if (!selectedCity) return; // ⛔ Stop if city is not selected

    const credential = {
      cityId: selectedCity,
      userId: LoginData?.userId
    };

    dispatch(getAgentList(credential));
  }, [selectedCity, LoginData, dispatch]);

  useEffect(() => {
    agentListApi();
  }, [agentListApi]);

  const { LoanTypeDrop, CityDrop, InternalSaleManagerFilterDrop } = useGlobalSelector();

  const handleGetState = useCallback(
    (optionVal) => {
      const data = {
        stateId: optionVal
      };
      dispatch(getCityData(data)); // city data
    },
    [dispatch]
  );

  useEffect(() => {
    handleGetState();
  }, [handleGetState]);
  // Saved Trigger Conditions - stores successfully created rules
  // Structure: Each rule maps conditions to agent assignments
  const [savedConditions, setSavedConditions] = useState([
    {
      id: `rule_${Date.now()}`,
      conditions: [],
      assignedAgents: [],
      createdDate: dayjs().format('YYYY-MM-DD'),
      status: 'Active'
    }
  ]);

  // Track which saved conditions are expanded
  const [expandedRules, setExpandedRules] = useState({});

  const addCondition = useCallback(() => {
    setConditions((prev) => [
      ...prev,
      {
        id: Date.now(),
        attribute: '',
        operator: '',
        value: '',
        logical: ''
      }
    ]);
  }, []);

  const removeCondition = useCallback((id) => {
    setConditions((prev) => prev.filter((c) => c.id !== id));
  }, []);

  const updateCondition = useCallback((id, field, value) => {
    setConditions((prev) => prev.map((c) => (c.id === id ? { ...c, [field]: value } : c)));
  }, []);

  const getDateError = useCallback(() => {
    if (ruleType === 'reassign') {
      return !validateDateRange(reassignData.startDate, reassignData.endDate);
    }
    return !validateDateRange(timeline.startDate, timeline.endDate);
  }, [ruleType, reassignData, timeline]);

  const handleSaveConditions = useCallback(() => {
    if (conditions.some((c) => !c.attribute || !c.operator || !c.value)) {
      dispatch(setApiError('Please fill all condition fields'));
      return;
    }

    const newConditions = conditions.map((c) => ({
      attribute: c.attribute,
      operator: c.operator,
      value: c.value,
      logical: c.logical
    }));

    setSavedConditions((prev) => {
      const updated = [...prev];
      updated[0] = {
        ...updated[0],
        conditions: [...updated[0].conditions, ...newConditions]
      };
      return updated;
    });

    dispatch(setApiError('Trigger condition added successfully'));

    setConditions([
      {
        id: Date.now(),
        attribute: '',
        operator: '',
        value: '',
        logical: 'AND'
      }
    ]);
  }, [conditions, dispatch]);

  const toggleRuleExpand = useCallback((ruleId) => {
    setExpandedRules((prev) => ({
      ...prev,
      [ruleId]: !prev[ruleId]
    }));
  }, []);

  const handleDeleteRule = useCallback(
    (ruleId) => {
      setSavedConditions((prev) => prev.filter((r) => r.id !== ruleId));
      dispatch(setApiError('Rule deleted successfully'));
    },
    [dispatch]
  );

  const handleNext = useCallback(() => {
    if (activeStep === 2 && !validateDateRange(timeline.startDate, timeline.endDate)) {
      dispatch(setApiError('Start Date must be before End Date'));
      return;
    }
    setActiveStep((prev) => prev + 1);
  }, [activeStep, timeline, dispatch]);

  const handleBack = useCallback(() => setActiveStep((prev) => prev - 1), []);
  // call center list
  const getApplyRuleApi = useCallback(async () => {
    if (!savedConditions.length) {
      dispatch(setApiError('Please save trigger conditions first'));
      return;
    }

    const latestRule = savedConditions[0];

    const payload = {
      mode: ruleType === 'new' ? 'New' : 'Reassign',
      dataSourceId: rowId,
      userId: LoginData?.userId,
      assignedTo: ruleType === 'new' ? currentAgent : reassignData.assignTo,
      reAssignedTo: ruleType === 'reassign' ? reassignData.reassignTo : null,
      scheduleStartDate: dayjs(
        ruleType === 'new' ? timeline.startDate : reassignData.startDate
      ).format('YYYY-MM-DD'),
      scheduleEndDate: dayjs(ruleType === 'new' ? timeline.endDate : reassignData.endDate).format(
        'YYYY-MM-DD'
      ),

      // 🔥 use saved rule, not reset form
      conditions: latestRule.conditions.map((c, index) => ({
        attribute: c.attribute,
        operator: mapOperator(c.operator),
        value: c.value,
        logicalOperator: c.logical || 'AND'
      }))
    };

    const result = await dispatch(saveApiData(DataSourceApi.saveApplyRule, payload));
    console.log('result222', result);
    if (result.success === true) {
      dispatch(setApiError('Allocation rule created successfully!'));
      setTimeout(() => navigate('/data/'), 1500);
    }
  }, [
    ruleType,
    savedConditions,
    currentAgent,
    reassignData,
    timeline,
    LoginData,
    rowId,
    dispatch,
    navigate
  ]);

  const handleFinish = useCallback(() => {
    if (getDateError()) {
      dispatch(setApiError('Start Date must be before End Date'));
      return;
    }
    getApplyRuleApi();
  }, [getDateError, dispatch, getApplyRuleApi]);

  const getInternalSaleManagerData = useCallback(() => {
    if (!LoginData?.companyId) return; // safety check
    const data = {
      companyId: LoginData?.companyId
    };
    dispatch(getInternalSaleManager(data));
  }, [LoginData]);

  useEffect(() => {
    getInternalSaleManagerData();
  }, [getInternalSaleManagerData]);

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            {/* Trigger Condition Form Section */}
            {conditions.map((c, index) => (
              <Box
                key={c.id}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(4, 1fr)'
                  },
                  columnGap: 3,
                  rowGap: 2.5,
                  mb: 3,
                  pb: 2.5,
                  borderBottom: index < conditions.length - 1 ? `1px solid ${THEME.border}` : 'none'
                }}
              >
                {/* Attributes dropdown - select which loan attribute to filter */}
                <Box>
                  <Typography sx={labelSx}>Attributes</Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={c.attribute || ''}
                      onChange={(e) => updateCondition(c.id, 'attribute', e.target.value)}
                      sx={selectSx}
                      displayEmpty
                      renderValue={(selected) => {
                        if (!selected) {
                          return <span style={{ color: '#9e9e9e' }}>Select Option</span>;
                        }
                        return selected;
                      }}
                    >
                      <MenuItem value="">
                        <em>Select Option</em>
                      </MenuItem>

                      {attributes.map((a) => (
                        <MenuItem key={a} value={a} sx={{ fontSize: 13 }}>
                          {String(a).toUpperCase()}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {/* Operator - comparison method for the condition */}
                <Box>
                  <Typography sx={labelSx}>Operator</Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={c.operator}
                      onChange={(e) => updateCondition(c.id, 'operator', e.target.value)}
                      sx={selectSx}
                      displayEmpty
                      renderValue={(selected) => {
                        if (!selected) {
                          return <span style={{ color: '#9e9e9e' }}>Select Option</span>;
                        }
                        return selected;
                      }}
                    >
                      {operators.map((o) => (
                        <MenuItem key={o} value={o} sx={{ fontSize: 13 }}>
                          {o}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {/* Value - the actual condition value to match */}
                <Box>
                  <Typography sx={labelSx}>Value</Typography>

                  {c.attribute === 'loanType' ? (
                    <FormControl fullWidth size="small">
                      <Select
                        value={c.value}
                        onChange={(e) => updateCondition(c.id, 'value', e.target.value)}
                        sx={selectSx}
                        displayEmpty
                      >
                        <MenuItem value="" disabled sx={{ fontSize: 13 }}>
                          Select Loan Type
                        </MenuItem>

                        {LoanTypeDrop?.map((item) => (
                          <MenuItem key={item.value} value={item.value} sx={{ fontSize: 13 }}>
                            {item.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Type Value"
                      value={c.value}
                      onChange={(e) => updateCondition(c.id, 'value', e.target.value)}
                      sx={fieldSx}
                    />
                  )}
                </Box>

                {/* Logical operator - AND/OR for multiple conditions */}
                <Box>
                  <Typography sx={labelSx}>AND/OR</Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={c.logical}
                      onChange={(e) => updateCondition(c.id, 'logical', e.target.value)}
                      sx={selectSx}
                    >
                      {logicalOps.map((l) => (
                        <MenuItem key={l} value={l} sx={{ fontSize: 13 }}>
                          {l}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            ))}

            {/* Save Conditions Button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                variant="contained"
                size="small"
                className="mainBtn mainBtnSm"
                onClick={handleSaveConditions}
              >
                Save
              </Button>
            </Box>

            {/* Saved Conditions Display Section */}
            {savedConditions.length > 0 && (
              <Box sx={{ mt: 5 }}>
                <Box
                  sx={{
                    position: 'relative',
                    mb: 3
                  }}
                >
                  {/* Section Header Pill */}
                  <Typography variant="body1" className="headingLineUp headingLineUpOut">
                    Saved Conditions
                  </Typography>

                  {/* Saved Rules Container */}
                  <Box className="viewLeadCard cardTopMinusMargin viewCardPadding">
                    {/* Iterate through each saved rule */}
                    {savedConditions.map((rule, idx) => (
                      <Box key={rule.id} sx={{ mb: idx < savedConditions.length - 1 ? 3 : 0 }}>
                        {/* Rule Header - Summary View */}
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            p: 2,
                            bgcolor: '#ffffff',
                            borderRadius: '8px',
                            border: `1px solid ${THEME.border}`,
                            mb: 2,
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              boxShadow: '0 2px 8px rgba(6, 182, 172, 0.1)'
                            }
                          }}
                          onClick={() => toggleRuleExpand(rule.id)}
                        >
                          {/* Rule Status Badge */}
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 2
                            }}
                          >
                            <Chip
                              label={rule.status}
                              size="small"
                              sx={{
                                bgcolor: rule.status === 'Active' ? '#4fbbc840' : '#fee2e2',
                                color: rule.status === 'Active' ? '#4fbbc8' : '#dc2626',
                                fontWeight: 600,
                                fontSize: 12
                              }}
                            />

                            {/* Condition Summary - shows first condition details */}
                            <Typography sx={{ fontSize: 13, color: THEME.textMuted }}>
                              {rule.conditions.length} condition
                              {rule.conditions.length > 1 ? 's' : ''} set
                            </Typography>
                          </Box>

                          {/* Action Buttons */}
                          <Box
                            sx={{
                              display: 'flex',
                              gap: 1,
                              alignItems: 'center'
                            }}
                          >
                            {/* Expand/Collapse Icon */}
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleRuleExpand(rule.id);
                              }}
                              sx={{
                                color: THEME.cyan,
                                transition: 'all 0.3s ease'
                              }}
                            >
                              {expandedRules[rule.id] ? (
                                <ExpandLessIcon fontSize="small" />
                              ) : (
                                <ExpandMoreIcon fontSize="small" />
                              )}
                            </IconButton>

                            {/* Delete Button */}
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteRule(rule.id);
                              }}
                              sx={{
                                color: '#ef4444',
                                transition: 'all 0.3s ease',
                                '&:hover': { bgcolor: '#fee2e2' }
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>

                        {/* Expanded Details Section */}
                        <Collapse in={expandedRules[rule.id]}>
                          <Box
                            sx={{
                              p: 2.5,
                              bgcolor: '#f8fafc',
                              borderRadius: '8px',
                              border: `1px dashed ${THEME.border}`,
                              mb: 2
                            }}
                          >
                            {/* Condition Details */}
                            <Box sx={{ mb: 2.5 }}>
                              <Typography
                                sx={{
                                  fontSize: 12,
                                  fontWeight: 700,
                                  color: THEME.textMuted,
                                  mb: 1.5,
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px'
                                }}
                              >
                                Trigger Conditions
                              </Typography>

                              {rule.conditions.map((cond, idx) => (
                                <Box
                                  key={idx}
                                  sx={{
                                    mb: 1,
                                    p: 1.5,
                                    bgcolor: '#ffffff',
                                    borderRadius: '6px',
                                    border: `1px solid ${THEME.border}`,
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                  }}
                                >
                                  <Box>
                                    <Typography
                                      sx={{
                                        fontSize: 13,
                                        fontWeight: 600,
                                        color: THEME.textDark
                                      }}
                                    >
                                      {cond.attribute}
                                    </Typography>
                                    <Typography
                                      sx={{
                                        fontSize: 12,
                                        color: THEME.textMuted,
                                        mt: 0.3
                                      }}
                                    >
                                      {cond.operator}: {cond.value}
                                    </Typography>
                                  </Box>
                                </Box>
                              ))}
                            </Box>

                            <Divider sx={{ my: 2 }} />

                            {/* Assigned Agents Section */}
                            <Box>
                              <Typography
                                sx={{
                                  fontSize: 12,
                                  fontWeight: 700,
                                  color: THEME.textMuted,
                                  mb: 1.5,
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px'
                                }}
                              >
                                Assigned Agents
                              </Typography>

                              {/* Agent Chips Display */}
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: 1
                                }}
                              >
                                {rule.assignedAgents.map((agentName) => (
                                  <Chip
                                    key={agentName}
                                    label={agentName}
                                    size="small"
                                    sx={{
                                      bgcolor: '#4fbbc8',
                                      color: '#ffffff',
                                      fontWeight: 600,
                                      borderRadius: '6px',
                                      fontSize: 12
                                    }}
                                  />
                                ))}
                              </Box>
                            </Box>

                            {/* Metadata */}
                            <Box
                              sx={{
                                mt: 2.5,
                                pt: 2,
                                borderTop: `1px solid ${THEME.border}`,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                              }}
                            >
                              <Typography sx={{ fontSize: 11, color: THEME.textMuted }}>
                                Created: {rule.createdDate}
                              </Typography>
                            </Box>
                          </Box>
                        </Collapse>

                        {/* Divider between rules */}
                        {idx < savedConditions.length - 1 && <Divider sx={{ my: 2 }} />}
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        );

      case 1:
        return (
          <Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)'
                },
                columnGap: 3,
                rowGap: 2.5,
                mb: 4
              }}
            >
              <Box>
                <Typography sx={labelSx}>Department</Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={personFilters.dept}
                    onChange={(e) =>
                      setPersonFilters({
                        ...personFilters,
                        dept: e.target.value
                      })
                    }
                    displayEmpty
                    sx={selectSx}
                    size="small"
                  >
                    <MenuItem value="" sx={{ fontSize: 13, color: '#9ca3af' }}>
                      Call Center
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box>
                <Typography sx={labelSx}>Location</Typography>
                <Grid item xs={4}>
                  <CustomSelect
                    name="cityName"
                    label=""
                    control={control}
                    errors={errors}
                    options={CityDrop || []}
                  />
                </Grid>
              </Box>

              <Box>
                <Typography sx={labelSx}>Agents</Typography>
                <Grid item xs={4}>
                  <Select
                    value={currentAgent}
                    disabled={!selectedCity}
                    onChange={(e) => {
                      const value = e.target.value;
                      setCurrentAgent(value);

                      if (!selectedAgents.includes(value)) {
                        setSelectedAgents((prev) => [...prev, value]);
                      }
                    }}
                    size="small"
                    displayEmpty
                    sx={selectSx}
                    style={{ width: '100%' }}
                    renderValue={(selected) => {
                      if (!selected) {
                        return (
                          <Typography sx={{ fontSize: 14, color: '#9ca3af' }}>
                            Select Agent
                          </Typography>
                        );
                      }

                      const agent = DataAgentList.find((a) => a.userId === selected);

                      return (
                        <Typography sx={{ fontSize: 14 }}>{agent?.userName || selected}</Typography>
                      );
                    }}
                  >
                    <MenuItem disabled value="">
                      <em>Select Agent</em>
                    </MenuItem>

                    {DataAgentList.map((a) => (
                      <MenuItem key={a.userId} value={a.userId} sx={{ fontSize: 13 }}>
                        {a.userName}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
              </Box>
            </Box>
          </Box>
        );

      case 2:
        return (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box>
              {getDateError() && (
                <Alert severity="warning" sx={{ mb: 3, borderRadius: '8px' }}>
                  Start Date must be before End Date
                </Alert>
              )}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(4, 1fr)'
                  },
                  columnGap: 3,
                  rowGap: 2.5
                }}
              >
                <Box>
                  {/* <Typography sx={labelSx}>Start Date</Typography> */}
                  <CustomDate
                    name="startDate"
                    control={control}
                    errors={errors}
                    label=""
                    isRequired
                    value={timeline.startDate}
                    onChange={(date) =>
                      setTimeline((prev) => ({
                        ...prev,
                        startDate: date
                      }))
                    }
                  />
                </Box>

                <Box>
                  {/* <Typography sx={labelSx}>End Date</Typography> */}
                  <CustomDate
                    name="endDate"
                    control={control}
                    errors={errors}
                    label=""
                    isRequired
                    value={timeline.endDate}
                    onChange={(date) =>
                      setTimeline((prev) => ({
                        ...prev,
                        endDate: date
                      }))
                    }
                  />
                </Box>
              </Box>
            </Box>
          </LocalizationProvider>
        );

      default:
        return null;
    }
  };

  // useEffect(() => {
  //   getApplyRuleApi(); // call center list
  // }, [getApplyRuleApi]);

  const getLoanMasterApi = useCallback(() => {
    const obj = {
      companyId: LoginData?.companyId
    };
    dispatch(getLoanTypeData(obj));
  }, [LoginData]);

  useEffect(() => {
    getLoanMasterApi(); // loan master list
  }, [getLoanMasterApi]);
  return (
    <Page title="Allocation Rules">
      <Box sx={{ bgcolor: THEME.pageBg, minHeight: '100vh', pb: 4 }}>
        {/* Sticky Header Section */}
        <Box
          sx={{
            bgcolor: 'white',
            borderBottom: '1px solid #e2e8f0',
            py: 1.5,
            px: 3,
            position: 'sticky',
            top: 0,
            zIndex: 1000,
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={2}>
              <IconButton
                onClick={() => navigate('/loans/data-management')}
                size="small"
                sx={{
                  transition: 'all 0.3s ease',
                  '&:hover': { bgcolor: '#f1f5f9' }
                }}
              >
                <ArrowBackIcon sx={{ fontSize: 20 }} />
              </IconButton>
              <Typography
                sx={{
                  fontWeight: 700,
                  color: THEME.textDark,
                  fontFamily: '"Poppins", sans-serif',
                  fontSize: '18px',
                  letterSpacing: '0.3px'
                }}
              >
                Allocation Rules
              </Typography>
            </Stack>
            <Breadcrumbs
              separator={
                <Typography sx={{ fontSize: 14, color: '#94a3b8', mx: 0.5 }}>&gt;</Typography>
              }
              sx={{ fontSize: '12px' }}
            >
              <Link
                color="inherit"
                onClick={() => navigate('/dashboard')}
                sx={{
                  cursor: 'pointer',
                  textDecoration: 'none',
                  color: '#64748b',
                  transition: 'color 0.3s ease',
                  '&:hover': { color: THEME.cyan }
                }}
              >
                Dashboard
              </Link>
              <Link
                color="inherit"
                sx={{
                  cursor: 'pointer',
                  textDecoration: 'none',
                  color: '#64748b',
                  transition: 'color 0.3s ease',
                  '&:hover': { color: THEME.cyan }
                }}
              >
                Repayment
              </Link>
              <Typography color="text.primary" sx={{ fontSize: '12px', color: '#64748b' }}>
                Rules
              </Typography>
              <Typography
                color="text.primary"
                sx={{
                  fontSize: '12px',
                  fontWeight: 600,
                  color: THEME.textDark
                }}
              >
                Allocation rules
              </Typography>
            </Breadcrumbs>
          </Stack>
        </Box>

        <Container maxWidth="xl" sx={{ mt: 4 }}>
          <Card
            elevation={0}
            sx={{
              p: 4,
              mb: 3
            }}
          >
            <Box sx={{ mb: 4 }}>
              <RadioGroup row value={ruleType} onChange={(e) => setRuleType(e.target.value)}>
                <Paper
                  elevation={0}
                  sx={{
                    border: '1px solid #4fbbc8',
                    borderRadius: '10px',
                    px: 2,
                    py: 0.1,
                    display: 'flex',
                    alignItems: 'center',
                    bgcolor: '#edf8f9',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 1px 3px rgba(6, 182, 172, 0.1)'
                  }}
                >
                  <FormControlLabel
                    value="reassign"
                    control={
                      <Radio
                        size="small"
                        sx={{
                          color: THEME.primary,
                          '&.Mui-checked': { color: THEME.primary }
                        }}
                      />
                    }
                    label={
                      <Typography
                        sx={{
                          fontSize: 14,
                          color: THEME.primary,
                          letterSpacing: '0.3px'
                        }}
                      >
                        Reassign
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    value="new"
                    control={
                      <Radio
                        size="small"
                        sx={{
                          color: THEME.primary,
                          '&.Mui-checked': { color: THEME.primary }
                        }}
                      />
                    }
                    label={
                      <Typography
                        sx={{
                          fontSize: 14,
                          color: THEME.primary,
                          letterSpacing: '0.3px'
                        }}
                      >
                        New
                      </Typography>
                    }
                  />
                </Paper>
              </RadioGroup>
            </Box>

            {ruleType === 'new' ? (
              <>
                <Stepper
                  activeStep={activeStep}
                  sx={{
                    mb: 5,
                    bgcolor: '#f8fafc',
                    p: 2,
                    borderRadius: '10px',
                    border: `1px solid ${THEME.border}`,
                    '& .MuiStepLabel-label': {
                      fontSize: 13,
                      fontWeight: 600,
                      color: '#64748b',
                      textTransform: 'capitalize'
                    },
                    '& .Mui-active .MuiStepLabel-label': {
                      color: THEME.cyan,
                      fontWeight: 700
                    },
                    '& .Mui-completed .MuiStepLabel-label': {
                      color: THEME.success
                    }
                  }}
                >
                  {steps.map((label) => (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>

                {renderStepContent(activeStep)}

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    mt: 5,
                    gap: 2
                  }}
                >
                  {activeStep > 0 && (
                    <Button
                      variant="outlined"
                      onClick={handleBack}
                      startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
                      className="mainBtn"
                    >
                      Back
                    </Button>
                  )}
                  {activeStep < steps.length - 1 ? (
                    <>
                      {' '}
                      <Button variant="contained" onClick={handleNext} className="mainBtn">
                        Next →
                      </Button>
                    </>
                  ) : (
                    <Button variant="contained" onClick={handleFinish} className="mainBtn">
                      Finish →
                    </Button>
                  )}
                </Box>
              </>
            ) : (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box>
                  {getDateError() && (
                    <Alert severity="warning" sx={{ mb: 3, borderRadius: '8px' }}>
                      Start Date must be before End Date
                    </Alert>
                  )}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, 1fr)',
                        md: 'repeat(4, 1fr)'
                      },
                      columnGap: 3,
                      rowGap: 2.5
                    }}
                  >
                    <Box>
                      <Typography sx={labelSx}>Assign To</Typography>
                      <FormControl fullWidth size="small">
                        <Select
                          fullWidth
                          displayEmpty
                          sx={selectSx}
                          value={reassignData.assignTo}
                          onChange={(e) =>
                            setReassignData({
                              ...reassignData,
                              assignTo: e.target.value
                            })
                          }
                          renderValue={(selected) => {
                            if (!selected) {
                              return (
                                <Typography sx={{ color: '#94a3b8', fontSize: 14 }}>
                                  Select Assign To
                                </Typography>
                              );
                            }

                            const agent = InternalSaleManagerFilterDrop?.find(
                              (a) => a.value === selected
                            );

                            return agent?.label || selected;
                          }}
                        >
                          <MenuItem value="" disabled>
                            Select Assign To
                          </MenuItem>

                          {InternalSaleManagerFilterDrop?.map((item) => (
                            <MenuItem key={item.value} value={item.value}>
                              {item.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    <Box>
                      <Typography sx={labelSx}>Reassign To</Typography>
                      <FormControl fullWidth size="small">
                        <Select
                          value={reassignData.reassignTo}
                          onChange={(e) =>
                            setReassignData({
                              ...reassignData,
                              reassignTo: e.target.value
                            })
                          }
                          size="small"
                          displayEmpty
                          sx={selectSx}
                          renderValue={(selected) => {
                            if (!selected) {
                              return (
                                <Typography sx={{ color: '#94a3b8', fontSize: 14 }}>
                                  Select Reassign To
                                </Typography>
                              );
                            }

                            const agent = InternalSaleManagerFilterDrop?.find(
                              (a) => a.value === selected
                            );

                            return agent?.label || selected;
                          }}
                        >
                          <MenuItem value="" disabled>
                            Select Reassign To
                          </MenuItem>

                          {InternalSaleManagerFilterDrop?.map((item) => (
                            <MenuItem key={item.value} value={item.value}>
                              {item.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    <Box>
                      <Typography sx={labelSx}>Start Date</Typography>
                      <CustomDate
                        name="reassignStartDate"
                        control={control}
                        errors={errors}
                        value={reassignData.startDate}
                        onChange={(date) =>
                          setReassignData((prev) => ({
                            ...prev,
                            startDate: date
                          }))
                        }
                      />
                    </Box>

                    <Box>
                      <Typography sx={labelSx}>End Date</Typography>

                      <Box className="">
                        <CustomDate
                          name="reassignEndDate"
                          control={control}
                          errors={errors}
                          value={reassignData.endDate}
                          onChange={(date) =>
                            setReassignData((prev) => ({
                              ...prev,
                              endDate: date
                            }))
                          }
                        />
                      </Box>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 5 }}>
                    <Button variant="contained" className="mainBtn" onClick={handleFinish}>
                      Submit
                    </Button>
                  </Box>
                </Box>
              </LocalizationProvider>
            )}
          </Card>
        </Container>
      </Box>
    </Page>
  );
}
